import { css } from '@emotion/react'
import { useState } from 'react'
import { Field, FormSpy } from 'react-final-form'
import dayjs from 'dayjs'
import _ from 'lodash'
import * as yup from 'yup'

import {
  Spacer,
  MultiButtonInput,
  DeleteModal,
  InputBox,
  NumberInput,
  CurrencyText,
  CurrencyInput,
  DatePicker,
  TextErrorField,
} from '../../../../components'
import { SECURITY_TYPES, VALUATION_METHODS, TAX_TYPES_TEXT } from '../../../../constants'
import AddBalanceButtons from '../components/AddBalanceButtons'
import {
  LightGreyText,
  InlineBlockContainer,
  MobileFullWidthContainer,
  SectionBox,
  FormLabel,
  FormText,
} from '../../styled'

export const rothSchema = yup.object({
  quantity: yup.number().nullable(),
  purchaseDate: yup.string().nullable(),
  totalCostBasis: yup.number().nullable(),
  valuationMethod: yup.number().nullable(),
  totalValue: yup
    .number()
    .nullable()
    .required('Balance is required')
    .min(1, 'Balance must be at least $1')
    .max(99999999, 'Balance must be less than $100,000,000'),
})

const RothForm = props => {
  const [deleteActive, setDeleteActive] = useState(false)

  const toggleDeleteModal = () => {
    setDeleteActive(prev => !prev)
  }

  const { hideNotInvested, onlyTaxType } = props
  const enableSharesControl =
    props.security?.price >= 1 &&
    _.includes([SECURITY_TYPES.Stock, SECURITY_TYPES['Mutual Fund']], props.security?.securityType)
  const valuationMethodButtons = [
    {
      text: '# of Shares',
      value: VALUATION_METHODS.marketPrice,
    },
    {
      text: 'Balance',
      value: VALUATION_METHODS.accountBalance,
    },
    !hideNotInvested && {
      text: 'Not Invested',
      value: VALUATION_METHODS.notInvested,
    },
  ].filter(item => !!item)

  return (
    <FormSpy subscription={{ values: true, touched: true, errors: true }}>
      {({ values, touched, form, errors }) => (
        <div>
          {/* Nothing to ask when this is the only tax type on offer; its balance is already open */}
          {!onlyTaxType && (
            <AddBalanceButtons
              value={values.roth ? 5 : false}
              onChange={(name, value, taxType) => {
                if (value === taxType) {
                  form.change('roth', {
                    quantity: undefined,
                    purchaseDate: dayjs().format('YYYY-MM-DD'),
                    // Roth investments don't have a cost basis, so we default to 0 here
                    totalCostBasis: 0,
                    totalValue: undefined,
                    // Even when the shares control is available, we default to entering a total balance
                    valuationMethod: VALUATION_METHODS.accountBalance,
                  })
                } else {
                  form.change('roth', null)
                }
              }}
              taxType={5}
            />
          )}

          {values.roth && (
            <div>
              <div>
                <Spacer space='11px' />
                <LightGreyText>{`${TAX_TYPES_TEXT[5]} balance info`}</LightGreyText>
                <Spacer space='8px' />
              </div>
              <div>
                <MobileFullWidthContainer>
                  <SectionBox>
                    <MobileFullWidthContainer>
                      {/* If the security type is a Stock or Mutual Fund and the security has a non-zero price, user can add based on # of Shares */}
                      {enableSharesControl && (
                        <Field
                          name='roth.valuationMethod'
                          render={({ input, meta }) => (
                            <MultiButtonInput
                              buttons={valuationMethodButtons}
                              mobileToggle
                              name={input.name}
                              value={input.value}
                              onBlur={input.onBlur}
                              error={meta.error}
                              showError={meta.touched}
                              margin='0'
                              onChange={(name, value) => {
                                if (value === VALUATION_METHODS.notInvested) {
                                  toggleDeleteModal()
                                } else {
                                  input.onChange(value)
                                  if (value === VALUATION_METHODS.accountBalance) {
                                    form.change('roth.quantity', 0)
                                  }
                                  if (value === VALUATION_METHODS.marketPrice) {
                                    const newSharesAmount = Math.round(
                                      (values.roth.totalValue || 0) / props.security?.price
                                    )
                                    form.change('roth.quantity', newSharesAmount)
                                    form.change(
                                      'roth.totalValue',
                                      Math.round(newSharesAmount * props.security?.price * 100) /
                                        100
                                    )
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      )}
                      <div>
                        {/* Shares or Balance Inputs */}
                        {values.roth.valuationMethod === VALUATION_METHODS.marketPrice && (
                          <InlineBlockContainer>
                            <FormLabel>Shares</FormLabel>
                            <InputBox>
                              <Field
                                name='roth.quantity'
                                format={value => (value === null ? undefined : value)}
                                parse={v => v}
                                render={({ input }) => (
                                  <NumberInput
                                    decimalScale={0}
                                    allowNegative={false}
                                    allowLeadingZeros={false}
                                    fontSize='14px'
                                    name={input.name}
                                    onBlur={input.onBlur}
                                    onChange={(name, value) => {
                                      input.onChange(value)
                                      form.change(
                                        'roth.totalValue',
                                        Math.round(props.security?.price * (value || 0) * 100) / 100
                                      )
                                    }}
                                    value={input.value}
                                    width='75px'
                                  />
                                )}
                              />
                            </InputBox>
                          </InlineBlockContainer>
                        )}

                        {values.roth.valuationMethod === VALUATION_METHODS.marketPrice && (
                          <InlineBlockContainer>
                            <FormLabel padding='30px'>Share price</FormLabel>
                            <FormText padding='0 0 0 30px'>
                              <CurrencyText
                                decimalScale={2}
                                fontSize='14px'
                                value={props.security?.price}
                              />
                            </FormText>
                          </InlineBlockContainer>
                        )}

                        <InlineBlockContainer>
                          <FormLabel
                            padding={
                              values.roth.valuationMethod === VALUATION_METHODS.marketPrice &&
                              '30px'
                            }>
                            Balance
                          </FormLabel>
                          {values.roth.valuationMethod === VALUATION_METHODS.accountBalance ? (
                            <InputBox>
                              <Field
                                name='roth.totalValue'
                                format={value => (value === null ? undefined : value)}
                                parse={v => v}
                                render={({ input }) => (
                                  <CurrencyInput
                                    decimalScale={2}
                                    fontSize='14px'
                                    name={input.name}
                                    onBlur={input.onBlur}
                                    onChange={(name, value) => input.onChange(value)}
                                    value={input.value}
                                    width='125px'
                                  />
                                )}
                              />
                            </InputBox>
                          ) : (
                            <FormText padding='0 0 0 30px'>
                              <CurrencyText
                                decimalScale={2}
                                fontSize='14px'
                                value={values.roth.totalValue || 0}
                              />
                            </FormText>
                          )}
                        </InlineBlockContainer>
                        <div
                          css={css`
                            padding-left: 10px;
                            padding-bottom: 10px;
                            align-self: center;
                          `}>
                          <TextErrorField
                            error={
                              values.roth.valuationMethod === VALUATION_METHODS.accountBalance ||
                              values.roth.quantity
                                ? _.get(errors, 'roth.totalValue', '')
                                : !values.roth.quantity
                                  ? 'Quantity is required'
                                  : ''
                            }
                            showError={
                              values.roth.valuationMethod === VALUATION_METHODS.accountBalance
                                ? touched['roth.totalValue']
                                : touched['roth.quantity']
                            }
                          />
                        </div>

                        {/* purchaseDate input */}
                        <div>
                          <FormLabel>As of date</FormLabel>
                          <InputBox
                            css={css`
                              padding-left: 6px;
                            `}>
                            <Field
                              name='roth.purchaseDate'
                              format={v => v}
                              parse={v => v}
                              render={({ input, meta }) => (
                                <DatePicker
                                  date={input.value}
                                  error={meta.error}
                                  fontSize='14px'
                                  handleChange={(name, value) => input.onChange(value)}
                                  maxYear={new Date().getFullYear()}
                                  minYear={1900}
                                  name={input.name}
                                  onBlur={input.onBlur}
                                  showError={meta.touched}
                                />
                              )}
                            />
                          </InputBox>
                        </div>
                      </div>
                    </MobileFullWidthContainer>
                  </SectionBox>
                </MobileFullWidthContainer>
              </div>

              <Spacer space='11px' />
              <DeleteModal
                handleConfirmation={() => {
                  form.change('roth', null)
                  toggleDeleteModal()
                }}
                isModalOpen={deleteActive}
                title='Set As Not Invested'
                toggleModal={toggleDeleteModal}
              />
            </div>
          )}
        </div>
      )}
    </FormSpy>
  )
}

export default RothForm
